import type { MouseEvent } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, ButtonProps, Menu, MenuItem, useMediaQuery, useTheme } from '@mui/material'
import { ExpandMore } from '@dhl/icons'
import { useSetLanguageDocument } from './useSetLanguageDocument'
import { useGetLanguageDescription } from './useGetLanguageDescription'

type Props = {
  locales: string[]
  overrideButtonColor?: boolean
  onSelectLanguage?: (language: string) => void
  size?: ButtonProps['size']
}

export const Language = ({ locales, onSelectLanguage, overrideButtonColor = false, size }: Props) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true })
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const { i18n } = useTranslation()
  const getLanguageDescription = useGetLanguageDescription()

  useSetLanguageDocument()

  const openLanguageSelection = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const selectLanguage = (language: string) => {
    i18n.changeLanguage(language)
    setAnchorEl(null)
    onSelectLanguage?.(language)
  }

  return (
    <>
      <Button
        endIcon={<ExpandMore />}
        onClick={openLanguageSelection}
        data-test="language-selector"
        size={size}
        variant="text"
        sx={{ color: overrideButtonColor ? 'grey.700' : 'primary' }}
      >
        {
          // reason: you can not be sure of the locale since they are typed as string...
          // FIX: make sure to type these!
          getLanguageDescription(i18n.language, isMobile ? 'short' : 'long')
        }
      </Button>

      <Menu id="language-selection" anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)} keepMounted>
        {locales.map((locale: string) => (
          <MenuItem key={`language-${locale}`} onClick={() => selectLanguage(locale)} data-test="language-item">
            {
              // reason: you can not be sure of the locale since they are typed as string...
              // FIX: make sure to type these!
              getLanguageDescription(locale, 'long')
            }
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
